import { Injectable, signal, computed } from '@angular/core';
import { CaseTypeEntity } from '../../domain/entities/case-type.entity';
import { CaseTypeDetailEntity } from '../../domain/entities/case-type-detail.entity';
import { CaseTypeCategoryEntity } from '../../domain/entities/case-type-category.entity';
import { CaseTypeDeletionImpactEntity } from '../../domain/entities/case-type-deletion-impact.entity';

@Injectable({ providedIn: 'root' })
export class CaseTypeState {
  private readonly _caseTypes = signal<CaseTypeEntity[]>([]);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _currentCaseType = signal<CaseTypeDetailEntity | null>(null);
  private readonly _saving = signal(false);
  private readonly _categories = signal<CaseTypeCategoryEntity[]>([]);
  private readonly _loadingCategories = signal(false);
  private readonly _errorCategories = signal<string | null>(null);
  private readonly _loadingChecklist = signal(false);
  private readonly _errorChecklist = signal<string | null>(null);
  private readonly _savingChecklist = signal(false);
  private readonly _savingCaseTypeStatus = signal(false);
  private readonly _isDeletingCaseType = signal(false);
  private readonly _loadingStatuses = signal(false);
  private readonly _savingStatus = signal(false);
  private readonly _errorStatuses = signal<string | null>(null);
  private readonly _caseTypesCount = signal<number>(0);
  private readonly _caseTypeDeletionImpact = signal<CaseTypeDeletionImpactEntity | null>(null);
  private readonly _loadingCaseTypeDeletionImpact = signal(false);
  private readonly _errorCaseTypeDeletionImpact = signal<string | null>(null);

  // Read-only signals
  public readonly caseTypes = this._caseTypes.asReadonly();
  public readonly loading = this._loading.asReadonly();
  public readonly error = this._error.asReadonly();
  public readonly currentCaseType = this._currentCaseType.asReadonly();
  public readonly saving = this._saving.asReadonly();
  public readonly categories = this._categories.asReadonly();
  public readonly loadingCategories = this._loadingCategories.asReadonly();
  public readonly errorCategories = this._errorCategories.asReadonly();
  public readonly loadingChecklist = this._loadingChecklist.asReadonly();
  public readonly errorChecklist = this._errorChecklist.asReadonly();
  public readonly savingChecklist = this._savingChecklist.asReadonly();
  public readonly savingCaseTypeStatus = this._savingCaseTypeStatus.asReadonly();
  public readonly isDeletingCaseType = this._isDeletingCaseType.asReadonly();
  public readonly loadingStatuses = this._loadingStatuses.asReadonly();
  public readonly savingStatus = this._savingStatus.asReadonly();
  public readonly errorStatuses = this._errorStatuses.asReadonly();
  public readonly caseTypesCount = this._caseTypesCount.asReadonly();
  public readonly caseTypeDeletionImpact = this._caseTypeDeletionImpact.asReadonly();
  public readonly loadingCaseTypeDeletionImpact = this._loadingCaseTypeDeletionImpact.asReadonly();
  public readonly errorCaseTypeDeletionImpact = this._errorCaseTypeDeletionImpact.asReadonly();

  // Computed signals
  public readonly hasCaseTypes = computed(() => this._caseTypes().length > 0);
  public readonly activeCaseTypes = computed(() =>
    this._caseTypes().filter((ct) => ct.isActive)
  );

  public setCaseTypes(caseTypes: CaseTypeEntity[]): void {
    this._caseTypes.set(caseTypes);
  }

  public setLoading(loading: boolean): void {
    this._loading.set(loading);
  }

  public setError(error: string | null): void {
    this._error.set(error);
  }

  public setCurrentCaseType(caseType: CaseTypeDetailEntity | null): void {
    this._currentCaseType.set(caseType);
  }

  public setSaving(saving: boolean): void {
    this._saving.set(saving);
  }

  public setLoadingCategories(loading: boolean): void {
    this._loadingCategories.set(loading);
  }

  public setErrorCategories(error: string | null): void {
    this._errorCategories.set(error);
  }

  public setCategories(categories: CaseTypeCategoryEntity[]): void {
    this._categories.set(categories);
  }

  public setLoadingChecklist(loading: boolean): void {
    this._loadingChecklist.set(loading);
  }

  public setErrorChecklist(error: string | null): void {
    this._errorChecklist.set(error);
  }

  public clearErrorChecklist(): void {
    this._errorChecklist.set(null);
  }

  public setSavingChecklist(saving: boolean): void {
    this._savingChecklist.set(saving);
  }

  public addCaseType(caseType: CaseTypeEntity): void {
    this._caseTypes.update((current) => [...current, caseType]);
  }

  public updateCaseType(updatedCaseType: CaseTypeEntity): void {
    this._caseTypes.update((current) =>
      current.map((ct) => (ct.id === updatedCaseType.id ? updatedCaseType : ct))
    );
  }

  public removeCaseType(id: string): void {
    this._caseTypes.update((current) => current.filter((ct) => ct.id !== id));
  }

  public setSavingCaseTypeStatus(saving: boolean): void {
    this._savingCaseTypeStatus.set(saving);
  }

  public setDeletingCaseType(deleting: boolean): void {
    this._isDeletingCaseType.set(deleting);
  }

  public setLoadingStatuses(loading: boolean): void {
    this._loadingStatuses.set(loading);
  }

  public setSavingStatus(saving: boolean): void {
    this._savingStatus.set(saving);
  }

  public setErrorStatuses(error: string | null): void {
    this._errorStatuses.set(error);
  }

  public setCaseTypesCount(count: number): void {
    this._caseTypesCount.set(count);
  }

  public clearErrorStatuses(): void {
    this._errorStatuses.set(null);
  }

  public clearCurrentCaseType(): void {
    this._currentCaseType.set(null);
  }

  public clearError(): void {
    this._error.set(null);
  }

  public setCaseTypeDeletionImpact(impact: CaseTypeDeletionImpactEntity | null): void {
    this._caseTypeDeletionImpact.set(impact);
  }

  public setLoadingCaseTypeDeletionImpact(loading: boolean): void {
    this._loadingCaseTypeDeletionImpact.set(loading);
  }

  public setErrorCaseTypeDeletionImpact(error: string | null): void {
    this._errorCaseTypeDeletionImpact.set(error);
  }

  public clearCaseTypeDeletionImpact(): void {
    this._caseTypeDeletionImpact.set(null);
    this._loadingCaseTypeDeletionImpact.set(false);
    this._errorCaseTypeDeletionImpact.set(null);
  }

  public clear(): void {
    this._caseTypes.set([]);
    this._loading.set(false);
    this._error.set(null);
    this._currentCaseType.set(null);
    this._saving.set(false);
    this._categories.set([]);
    this._loadingChecklist.set(false);
    this._errorChecklist.set(null);
    this._savingChecklist.set(false);
    this._loadingStatuses.set(false);
    this._savingStatus.set(false);
    this._errorStatuses.set(null);
    this._caseTypeDeletionImpact.set(null);
    this._loadingCaseTypeDeletionImpact.set(false);
    this._errorCaseTypeDeletionImpact.set(null);
  }
}
